import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { getDatabase, ref, get, child } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

// Type definition
interface MaterialDefinition {
    translations: { sv: string };
}

interface MaterialsContextType {
    materials: Record<string, MaterialDefinition>;
    getTranslation: (key: string | undefined) => string;
    refresh: () => Promise<void>;
}

// Module-level cache for non-React access
let materialsCache: Record<string, MaterialDefinition> = {};

/**
 * Gets the Swedish translation for a material key.
 * Can be used outside React components.
 * @param key - The material key (e.g., "wood", "brick")
 * @returns The Swedish translation, or "Odefinierat" if the material can't be
 *          resolved — the raw key is internal metadata and must never be shown.
 */
export function getMaterialTranslation(key: string | undefined): string {
    if (!key) return "Odefinierat";
    return materialsCache[key]?.translations?.sv || "Odefinierat";
}

const MaterialsContext = createContext<MaterialsContextType | undefined>(undefined);

/**
 * Hook to access materials context.
 * Must be used within a MaterialsProvider.
 */
export const useMaterials = () => {
    const context = useContext(MaterialsContext);
    if (!context) {
        throw new Error('useMaterials must be used within a MaterialsProvider');
    }
    return context;
};

/**
 * Provider component that loads materials from Firebase config.
 * Non-blocking: app continues with fallback if load fails.
 */
export const MaterialsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [materials, setMaterials] = useState<Record<string, MaterialDefinition>>({});
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

    // Listen for auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });
        return () => unsubscribe();
    }, []);

    // Fetch materials function (also used for refresh)
    const fetchMaterials = useCallback(async () => {
        try {
            const db = getDatabase();
            const snapshot = await get(child(ref(db), 'config/materials'));
            if (snapshot.exists()) {
                const data = snapshot.val();
                setMaterials(data);
                materialsCache = data; // Sync to module cache
            } else {
                setMaterials({});
                materialsCache = {};
            }
        } catch (err) {
            console.error("Error fetching materials:", err);
            // Non-blocking: app continues with empty materials
        }
    }, []);

    // Fetch materials when authenticated
    useEffect(() => {
        if (isAuthenticated === null || !isAuthenticated) return;
        fetchMaterials();
    }, [isAuthenticated, fetchMaterials]);

    // Refresh function for manual re-fetch
    const refresh = useCallback(async () => {
        await fetchMaterials();
    }, [fetchMaterials]);

    const getTranslation = useCallback((key: string | undefined): string => {
        if (!key) return "Odefinierat";
        return materials[key]?.translations?.sv || key;
    }, [materials]);

    const value = useMemo(() => ({ materials, getTranslation, refresh }), [materials, getTranslation, refresh]);

    return (
        <MaterialsContext.Provider value={value}>
            {children}
        </MaterialsContext.Provider>
    );
};
